import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PartOne from './PartOne';
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import OrganisationLogo from '../../../components/OrganisationLogo';

// TYPES
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	DomainCertificationType,
	DomainProfileType,
	OrganisationProfileCheckProfileCheckResult,
	OrganisationFindOrganisationSummary
} from '../../../api/models';

interface CertificationVisitorTypes {
	organisationData: OrganisationFindOrganisationSummary | undefined;
	profileData: { label?: string; value?: DomainProfileType } | undefined;
	profileCheck: OrganisationProfileCheckProfileCheckResult | undefined;
}

export interface VisitorCertificationTypes {
	visitorType?: string;
	visitPurpose?: string;
	businessName?: string;
	existingWWCCRegistration?: AccountJoinOrganisationCertification[];
	workingWithChildren: AccountJoinOrganisationCertification;
	workingWithChildrenStatus?: string;
	acknowledge: boolean;
}

const CertificationVisitor: React.FC<CertificationVisitorTypes> = ({ organisationData, profileData, profileCheck }) => {
	const [formData, setFormData] = React.useState<VisitorCertificationTypes>({
		visitorType: '',
		visitPurpose: '',
		businessName: '',
		existingWWCCRegistration: [
			{
				certificationType: DomainCertificationType.WorkingWithChildrenCheck,
				state: '',
				expiry: '',
				certificationNumber: '',
				createAttachments: [],
				userAcknowledgement: false
			}
		],
		workingWithChildren: {
			certificationType: DomainCertificationType.WorkingWithChildrenCheck,
			state: '',
			expiry: '',
			certificationNumber: '',
			createAttachments: [],
			userAcknowledgement: false
		},
		acknowledge: false
	});
	const [formSection, setFormSection] = React.useState<string>('partOne');

	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [existingCertification, setExistingCertification] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountExistingCertifications();

		if (response.isError) {
			setIsLoading(false);
		} else {
			const { result = [] } = response;
			// Expired WWCCs are kept so they can be displayed; only the "use existing" option checks validity
			setExistingCertification(result.filter((item) => item.certificationType === 'WorkingWithChildrenCheck'));

			setIsLoading(false);

			setShowError(false);
		}
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMessage message={formErrorMessage} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="header row">
									<div className="section-header offset-col-2 col-8">
										{formSection === 'success' ? null : (
											<>
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">Registering with {organisationData?.name} as a Site Visitor</h2>

												{profileCheck?.isPastMember ? (
													<p className="sub-purple-text mtop-20">
														We've detected you've had a past role with this organisation. We'll reinstate your account
														with this organisation. Please select your new role and update any required documents.
													</p>
												) : (
													<p className="sub-grey-text">Please provide additional certification information.</p>
												)}
											</>
										)}
									</div>
								</div>

								{formSection === 'partOne' ? (
									<PartOne
										setFormSection={setFormSection}
										setFormData={setFormData}
										formData={formData}
										loading={isLoading}
										existingCertification={existingCertification}
										setIsLoading={setIsLoading}
										setFormErrorMessage={setFormErrorMessage}
										setShowError={setShowError}
										organisationData={organisationData}
										profileData={profileData}
									/>
								) : formSection === 'success' ? (
									<CertificationSuccess organisationName={organisationData && organisationData.name} />
								) : null}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationVisitor;
